import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divert, DivertsAttribute, HusqiotbusConfig } from 'models/husqiotbus';
import Card from 'components/UI/Card';
import { ButtonGrid, FiveColumnGrid } from './styled';
import AccordionHeaderItem from 'components/UI/Accordion/AccordionHeaderItem';
import InputField from 'components/UI/InputField';
import SelectField from 'components/UI/SelectField';
import {
  divertTypeOptions,
  requestResponse,
} from 'services/husqiotBusService/defaultConfigService';
import { useForm } from 'react-hook-form';
import DivertTable from '../DivertsTable';
import Button from 'components/UI/Button';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  addDivert,
  configSelector,
} from 'store/husqiotbusState/husqiotbusState';
import { showErrorToast } from 'utils/toast';
import EnvironmentSelector from '../EnvironmentSelector';
import { getDivertVersion } from 'services/husqiotBusService/husqiotBusService';

function getEnvironments(dev: any, qa: any, live: any) {
  const environments = [];
  if (dev) {
    environments.push('dev');
  }
  if (qa) {
    environments.push('qa');
  }
  if (live) {
    environments.push('live');
  }
  return environments;
}

type Props = {
  headingId: string;
};

const DivertsCard: FC<Props> = ({ headingId }) => {
  const intl = useIntl();
  const { register, watch, setValue } = useForm();
  const dispatch = useAppDispatch();

  const { selectedService }: HusqiotbusConfig = useAppSelector(configSelector);

  // Watch
  const [serviceName, direction, filter, dev, qa, live] = watch([
    DivertsAttribute.ServiceName,
    DivertsAttribute.Direction,
    DivertsAttribute.Filter,
    DivertsAttribute.Dev,
    DivertsAttribute.Qa,
    DivertsAttribute.Live,
  ]);

  const onSelect = useCallback(
    (name: DivertsAttribute) => (value: string) => {
      setValue(DivertsAttribute.Filter, '');
      setValue(name, value);
    },
    [setValue]
  );

  const onCheckboxChange = useCallback(
    (name: DivertsAttribute) => (value: any) => {
      setValue(name, value.target.checked);
    },
    [setValue]
  );

  const onClick = useCallback(() => {
    const existingDivertWithSameNameAndDir = selectedService?.diverts.find(
      (divert) =>
        divert.serviceName === serviceName && divert.type === direction
    );
    if (
      !serviceName ||
      serviceName === '' ||
      !direction ||
      existingDivertWithSameNameAndDir ||
      (!dev && !qa && !live)
    ) {
      showErrorToast(intl.formatMessage({ id: 'husqiotbus.divert_error' }));
      return;
    }
    const environments = getEnvironments(dev, qa, live);
    const version = getDivertVersion(
      selectedService?.existingDiverts || [],
      serviceName,
      direction
    );

    const divert: Divert = {
      serviceName,
      type: direction,
      filter,
      environments,
      version,
    };
    dispatch(addDivert(divert));
  }, [
    dispatch,
    serviceName,
    direction,
    filter,
    selectedService,
    intl,
    dev,
    qa,
    live,
  ]);

  return (
    <Card>
      <AccordionHeaderItem key={headingId} titleId={headingId}>
        <FiveColumnGrid>
          <InputField
            key={'husqiotbus.service_name'}
            description="husqiotbus.service_name"
            register={register(DivertsAttribute.ServiceName, {
              required: { value: true, message: 'required' },
            })}
          />
          <SelectField
            name={DivertsAttribute.Direction}
            onSelect={onSelect(DivertsAttribute.Direction)}
            label={intl.formatMessage({ id: 'husqiotbus.direction' })}
            options={divertTypeOptions}
          />
          {direction !== requestResponse && [
            <InputField
              key={'husqiotbus.filter'}
              description="husqiotbus.filter"
              register={register(DivertsAttribute.Filter)}
            />,
          ]}
          <EnvironmentSelector onCheckboxChange={onCheckboxChange} />
          <ButtonGrid>
            <Button onClick={onClick} marginRight>
              <FormattedMessage id="husqiotbus.add_divert" />
            </Button>
          </ButtonGrid>
        </FiveColumnGrid>
        <DivertTable diverts={selectedService?.diverts || []} />
      </AccordionHeaderItem>
    </Card>
  );
};

export default DivertsCard;
